import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteCampaign, fetchCampaigns, patchCampaign } from '@/services/campaign';
import { CreateCampaignButton } from '@/components/button/create-campaign-button';
import { CampaignDataTable } from '@/routes/console/_authenticated/_layout/campaigns/-features/campaign-data-table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { ViewToggle } from '@/routes/console/_authenticated/_layout/campaigns/-features/view-toggle';
import { CampaignCardGrid } from '@/routes/console/_authenticated/_layout/campaigns/-features/campaign-card-grid';
import { toast } from 'sonner';
import { DATE_FORMAT_ISO, DISPLAY_DATE_FORMAT } from '@/constants/date';
import { AnimatePresence, motion } from 'framer-motion';
import { ListRestart, Search } from 'lucide-react';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { errorHandler } from '@/lib/utils';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import { FiltersRow } from '@/components/filter-row';
const defaultFrom = () => format(startOfDay(subDays(new Date(), 6)), DATE_FORMAT_ISO);
const defaultTo = () => format(endOfDay(new Date()), DATE_FORMAT_ISO);
const searchParamsSchema = z.object({
    name: z.string().optional(),
    status: z.enum(['all', 'draft', 'active', 'archived']).default('active'),
    viewMode: z.enum(['table', 'card']).default('table'),
    excludeDevices: z.array(z.string()).default(['bot']),
    excludeBrowsers: z.array(z.string()).default(['bot']),
    from: z.string().default(defaultFrom),
    to: z.string().default(defaultTo),
});
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/')({
    component: Campaigns,
    validateSearch: zodValidator(searchParamsSchema),
});
function Campaigns() {
    const { t } = useTranslation();
    const { workspace } = useAuthenticationState();
    const { name, status, viewMode, excludeDevices, excludeBrowsers, from, to } = Route.useSearch();
    const navigate = Route.useNavigate();
    const fromDate = React.useMemo(() => new Date(from), [from]);
    const toDate = React.useMemo(() => new Date(to), [to]);
    const { data = [], refetch, isFetching, } = useQuery({
        queryKey: ['campaigns', name, status, excludeDevices, excludeBrowsers, from, to],
        queryFn: () => fetchCampaigns({
            name: name || undefined,
            status: status === 'all' ? undefined : status,
            excludeDevices,
            excludeBrowsers,
            from: new Date(from),
            to: new Date(to),
        }),
    });
    const { mutate } = useMutation({
        mutationFn: deleteCampaign,
        onSuccess: (resp) => {
            toast.success(resp.message);
            refetch();
        },
        onError: errorHandler,
    });
    const { mutate: patch, isPending: isUpdatingStatus } = useMutation({
        mutationFn: patchCampaign,
        onSuccess: (resp) => {
            toast.success(resp.message);
            refetch();
        },
        onError: errorHandler,
    });
    const handleChangeStatus = React.useCallback((publicId, next) => {
        patch({ publicId, data: { status: next } });
    }, [patch]);
    const handleReset = React.useCallback(() => {
        navigate({
            search: {
                name: '',
                status: 'active',
                viewMode,
                excludeDevices,
                excludeBrowsers,
                from: defaultFrom(),
                to: defaultTo(),
            },
        });
    }, [navigate, viewMode, excludeDevices, excludeBrowsers]);
    const handleRangeChange = React.useCallback((newFrom, newTo) => {
        navigate({
            search: {
                name,
                status,
                viewMode,
                excludeDevices,
                excludeBrowsers,
                from: format(startOfDay(newFrom), DATE_FORMAT_ISO),
                to: format(endOfDay(newTo), DATE_FORMAT_ISO),
            },
        });
    }, [name, status, viewMode, excludeDevices, excludeBrowsers, navigate]);
    const viewVariants = React.useMemo(() => ({
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
    }), []);
    const onNavigate = React.useCallback((publicId) => {
        navigate({ to: '/console/campaigns/$publicId', params: { publicId } });
    }, [navigate]);
    const portfolioStats = React.useMemo(() => summarize(data), [data]);
    return (<div className="w-full space-y-6 p-6">
            {/* HERO HEADER */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold tracking-tight">
                        {t('campaigns.title', { ns: 'glossary', defaultValue: 'Campaigns' })}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {t('campaigns.subtitle', {
            ns: 'glossary',
            defaultValue: 'All campaigns in this workspace.',
        })}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-muted-foreground tabular-nums">
                        {format(fromDate, 'MMM d')} – {format(toDate, 'MMM d, yyyy')}
                    </span>
                    <CreateCampaignButton />
                </div>
            </div>

            <FiltersRow from={fromDate} to={toDate} setRange={handleRangeChange}/>

            {/* PORTFOLIO STAT STRIP */}
            <Card className="border-border/60 bg-card/40 backdrop-blur-sm">
                <CardContent className="grid divide-y divide-border/40 p-0 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
                    {[
            {
                label: t('campaigns.stats.total', { defaultValue: 'Total campaigns' }),
                value: portfolioStats.total,
            },
            {
                label: t('campaigns.stats.active', { defaultValue: 'Active' }),
                value: portfolioStats.active,
                hint: portfolioStats.total
                    ? `${Math.round((portfolioStats.active / portfolioStats.total) * 100)}%`
                    : undefined,
            },
            {
                label: t('campaigns.stats.draft', { defaultValue: 'Draft' }),
                value: portfolioStats.draft,
            },
            {
                label: t('campaigns.stats.total_scans', { defaultValue: 'Total scans' }),
                value: portfolioStats.totalScans.toLocaleString(),
                hint: portfolioStats.total > 0
                    ? `${Math.round(portfolioStats.totalScans / portfolioStats.total).toLocaleString()} avg`
                    : undefined,
            },
        ].map((stat, i) => (<div key={i} className="flex items-start justify-between gap-3 p-4">
                            <div className="min-w-0 space-y-1">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">{stat.label}</p>
                                <p className="text-xl font-semibold tabular-nums text-foreground sm:text-2xl">
                                    {stat.value}
                                </p>
                                {stat.hint ? (<p className="truncate text-xs text-muted-foreground">{stat.hint}</p>) : null}
                            </div>
                        </div>))}
                </CardContent>
            </Card>

            {/* FILTER BAR */}
            <div className="rounded-xl border border-border/60 bg-card/40 p-3 backdrop-blur-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-1 flex-wrap items-center gap-2">
                        <div className="relative w-full max-w-sm">
                            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                            <Input className="pl-8" placeholder={t('campaigns.placeholders.search_name', { ns: 'glossary' })} value={name ?? ''} onChange={(e) => navigate({
            search: {
                status,
                viewMode,
                name: e.target.value,
                excludeDevices,
                excludeBrowsers,
                from,
                to,
            },
        })}/>
                        </div>

                        <Select value={status} onValueChange={(next) => navigate({
            search: {
                name,
                viewMode,
                status: next,
                excludeDevices,
                excludeBrowsers,
                from,
                to,
            },
        })}>
                            {/* Reserve enough width for "Archived" (the
            longest option) so the trigger doesn't
            resize as the user changes selection. */}
                            <SelectTrigger className="min-w-[8rem]">
                                <SelectValue placeholder={t('status')}/>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('all')}</SelectItem>
                                <SelectItem value="draft">{t('draft')}</SelectItem>
                                <SelectItem value="active">{t('active')}</SelectItem>
                                <SelectItem value="archived">{t('archived')}</SelectItem>
                            </SelectContent>
                        </Select>

                        <Button variant="ghost" size="sm" onClick={handleReset} disabled={isFetching} className="text-muted-foreground">
                            <ListRestart className="mr-1 h-4 w-4"/>
                            {t('reset')}
                        </Button>
                    </div>

                    <ViewToggle value={viewMode} onChange={(next) => navigate({
            search: { name, status, viewMode: next, excludeDevices, excludeBrowsers, from, to },
        })}/>
                </div>
            </div>

            {/* LIST */}
            <AnimatePresence mode="wait">
                <motion.div key={viewMode} variants={viewVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.18, ease: 'easeOut' }}>
                    {data.length === 0 && !isFetching ? (<Card className="border-dashed border-border/60 bg-muted/10">
                            <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                                <p className="text-sm text-muted-foreground">
                                    {name || status !== 'all'
                ? t('campaigns.empty.filtered', {
                    ns: 'glossary',
                    defaultValue: 'No campaigns match the current filters.',
                })
                : t('campaigns.empty.workspace', {
                    ns: 'glossary',
                    defaultValue: 'No campaigns in this workspace yet.',
                })}
                                </p>
                                <CreateCampaignButton />
                            </CardContent>
                        </Card>) : viewMode === 'table' ? (<CampaignDataTable data={data} canClone={!!workspace?.subscription.plan.allowClone} onView={onNavigate} onDelete={(publicId) => mutate(publicId)} onChangeStatus={(publicId, next) => handleChangeStatus(publicId, next)} isUpdatingStatus={isUpdatingStatus} dateFormat={DISPLAY_DATE_FORMAT}/>) : (<CampaignCardGrid data={data} canClone={!!workspace?.subscription.plan.allowClone} onView={onNavigate} onDelete={(publicId) => mutate(publicId)} onChangeStatus={(publicId, next) => handleChangeStatus(publicId, next)} isUpdatingStatus={isUpdatingStatus} dateFormat={DISPLAY_DATE_FORMAT}/>)}
                </motion.div>
            </AnimatePresence>
        </div>);
}
function summarize(campaigns) {
    return campaigns.reduce((acc, c) => {
        acc.total += 1;
        if (c.status === 'active')
            acc.active += 1;
        else if (c.status === 'draft')
            acc.draft += 1;
        else if (c.status === 'archived')
            acc.archived += 1;
        acc.totalScans += c.totalScans ?? 0;
        return acc;
    }, { total: 0, active: 0, draft: 0, archived: 0, totalScans: 0 });
}
